<template>
    <div v-if="question && question.id" class="pb-8">
        <p class="mb-1">
            {{ questionNumber }}. {{ question.content }}
        </p>
        <div v-if="question.type == 'Multiple Choice'">
            <div v-for="choice in question.answerChoices" :key="choice.id" class="pl-4 pb-1">
                <d-radio v-model="selectedAnswer" :native-value="choice.id">
                    {{ choice.content }}
                </d-radio>
            </div>
        </div>
        <div v-if="question.type == 'Multiple Answer'">
            <div class="d-flex flex-column" v-for="choice in question.answerChoices" :key="choice.id">
                <d-checkbox v-model="answerChoiceIds" :native-value="choice.id" class="ml-2">
                    <div class="flex items-center">
                        <span>{{ choice.content }}</span>
                    </div>
                </d-checkbox>
            </div>
        </div>
        <div v-if="question.type == 'Drop Down'">
            <d-select placeholder="Select an answer" v-model="selectedAnswer">
                <option v-for="choice in question.answerChoices" :key="choice.id" :value="choice.id" required>
                    {{ choice.content }}
                </option>
            </d-select>
        </div>
        <div v-if="question.type == 'File Upload'">
            <d-field label="Upload file(s) here - you can select multiple if desired, max 20mb each">
                <input type="file" v-on:change="onFileInputChange" multiple max-size="20971520">
            </d-field>
            <div v-if="isLoading" class="text-center my-4">
                <d-loader />
            </div>
        </div>
        <div v-if="question.type == 'Text'">
            <d-field label="Write your answer here">
                <d-input v-model="textAnswer" type="textarea" />
            </d-field>
        </div>
        <button class="ml-2 mt-2" v-if="!isReading" @click="readQuestionAloud"><u>Read Aloud</u></button>
        <button class="ml-2 mt-2" v-if="isReading" @click="stopReadingQuestionAloud"><u>Stop Reading</u></button>

    </div>
</template>

<script>
  import axios from 'axios';
  import debounce from 'lodash/debounce';

  export default {
    props: {
      question: {
        type: Object,
        default: () => ({}),
      },

      quizResult: {
        type: Object,
        default: () => ({}),
      },

      questionNumber: Number,
    },

    data() {
      return {
        selectedAnswer: '',
        textAnswer: '',
        files: [],
        answerChoiceIds: [],
          isLoading: false,
        isReading: false
      };
    },

    watch: {
      selectedAnswer() {
        this.debounceSubmitAnswer();
      },
      textAnswer() {
        this.debounceSubmitAnswer();
      },
      files() {
        this.debounceSubmitAnswer();
      },
      answerChoiceIds() {
        this.debounceSubmitAnswer();
      }
    },

    mounted() {
      this.checkForSelectedAnswer();
    },

    methods: {
      onFileInputChange(event) {
          // Update the files data property with the selected files
          this.files = Array.from(event.target.files);
      },

      debounceSubmitAnswer: debounce(function () {
        if (!this.question || !this.question.id) {
          return;
        }
        
        this.isLoading = true;
        const formData = new FormData();
        formData.append('quizQuestionId', this.question.id);
        formData.append('quizQuestionAnswerChoiceId', this.selectedAnswer);
        formData.append('textAnswer', this.textAnswer);
        formData.append('answerChoiceIds', this.answerChoiceIds);

        // Append the files to the FormData object
        for (let i = 0; i < this.files.length; i++) {
            formData.append('files', this.files[i]);
        }

        axios.post('/api/content-listings/items/quiz/answers', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(() => {
            this.isLoading = false;
            // Optional: Show success message for answer submission
            // this.$toast.success('Answer saved successfully');
        }).catch((error) => {
            this.isLoading = false;
            console.error('Error saving answer:', error);
            this.$toast.error('Failed to save your answer. Please try again.');
        });
      }, 500),

      checkForSelectedAnswer() {
        if (!this.quizResult || !this.quizResult.questionAnswers || !this.question) {
          return;
        }
        
        const answer = this.quizResult.questionAnswers.find((m) => m.quizQuestionId === this.question.id);
        if (answer) {
          this.answerChoiceIds = answer.selectedAnswerIds || [];
          this.selectedAnswer = answer.selectedAnswerId || '';
          this.textAnswer = answer.selectedAnswer || '';
        }
      },

        stopReadingQuestionAloud() {
            this.isReading = false;
            window.speechSynthesis.cancel();
        },

        readQuestionAloud() {
            const questionText = `${this.questionNumber}. ${this.question.content}`;
            let speechText = questionText;

            if (this.question.type === 'Multiple Choice') {
                speechText += `Choose from the following options: `;
                this.question.answerChoices.forEach((choice, index) => {
                    speechText += `${index + 1}. ${choice.content}. `;
                });
            } else if (this.question.type === 'Multiple Answer') {
                speechText += `Select all that apply: `;
                this.question.answerChoices.forEach((choice, index) => {
                    speechText += `${index + 1}. ${choice.content}. `;
                });
            } else if (this.question.type === 'Drop Down') {
                speechText += `Select one option from the following: `;
                this.question.answerChoices.forEach((choice, index) => {
                    speechText += `${index + 1}. ${choice.content}. `;
                });
            } else if (this.question.type === 'Text') {
                speechText += `Write your answer in the text field provided. `;
            }
            else if (this.question.type === 'File Upload') {
                speechText += `Use the choose files button to select as many files to upload as desired. `;
            }

            const utterance = new SpeechSynthesisUtterance(speechText);
            this.isReading = true;
            window.speechSynthesis.speak(utterance);
        }

    },
  };
</script>

<style scoped>
/* Make question text darker and more prominent */
.pb-8 > p {
  color: #1f2937 !important; /* Dark gray text */
  font-weight: 600 !important; /* Semi-bold for better readability */
}

/* Make radio button outlines darker and more visible */
:deep(.radio) {
  border: 2px solid #374151 !important; /* Dark gray border */
  border-radius: 50%;
}

:deep(.radio:checked) {
  border-color: #1f2937 !important; /* Even darker when checked */
  background-color: #1f2937 !important;
}

/* Make radio button labels darker */
:deep(.label span) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Alternative selector for radio button text */
:deep(.radio ~ span) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Target the ml-2 span specifically */
:deep(span.ml-2) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Make checkbox outlines darker for multiple answer questions */
:deep(.checkbox) {
  border: 2px solid #374151 !important;
}

:deep(.checkbox:checked) {
  border-color: #1f2937 !important;
  background-color: #1f2937 !important;
}

/* Make checkbox labels darker */
:deep(.checkbox ~ span) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Target checkbox labels more specifically */
:deep(.ml-2 span) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Make dropdown text darker */
:deep(.select select) {
  color: #1f2937 !important;
  border: 2px solid #374151 !important;
}

/* Make textarea text darker */
:deep(.input[type="textarea"]) {
  color: #1f2937 !important;
  border: 2px solid #374151 !important;
}

/* Make file input labels darker */
:deep(.field-label) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Make read aloud buttons more visible */
button {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

button u {
  text-decoration-color: #1f2937 !important;
}
</style>